import { useLocationSlugStore } from 'src/features/locations/state/useLocationSlugStore'
import { CompositeProduct, LocationProductRelationship } from 'src/types/schema/graphql'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

const getLocationAvailability = (
    product: CompositeProduct,
    locationId: string
): LocationProductRelationship => {
    return product.locationRelationships?.find((item) => item?.location?.id === locationId)
}

interface IBrewsListStore {
    products: CompositeProduct[]
    setProducts: (products: CompositeProduct[]) => void
    paginatedProducts: CompositeProduct[][]
    setPaginatedProducts: (products: CompositeProduct[][]) => void
    page: number
    setPage: (page: number) => void
    pageMax: number
    setPageMax: (pageMax: number) => void
    isPrevDisabled: boolean
    setIsPrevDisabled: (isPrevDisabled: boolean) => void
    isNextDisabled: boolean
    setIsNextDisabled: (isNextDisabled: boolean) => void
    filteredProducts: CompositeProduct[]
    setFilteredProducts: (products: CompositeProduct[], value: number) => void
    createPaginatedArray: (products: CompositeProduct[], value: number) => CompositeProduct[][]
    styleFilter: string
    setStyleFilter: (styleFilter: string) => void
    searchTerm: string
    setSearchTerm: (searchTerm: string) => void
    availabilityFilter: string
    setAvailabilityFilter: (availabilityFilter: string) => void
    availableProducts: LocationProductRelationship[]
    populateAvailableProducts: (locationId: string, brandId: string) => Promise<void>
    resetBrewListStore: () => void
}
export const useBrewsListStore = create<IBrewsListStore>()(
    devtools(
        (set, get) => ({
            products: [],
            availableProducts: [],
            populateAvailableProducts: async () => {
                const availableProducts =
                    useLocationSlugStore.getState().locationData?.productRelationships
                set({ availableProducts: availableProducts })
            },
            setProducts: (products) => {
                const availableProducts = get().availableProducts
                if (availableProducts?.length > 0) {
                    const productsWithAvailability = products.map((product) => {
                        const relatedProduct = availableProducts.findIndex(
                            (item) => item?.product?.id === product?.id
                        )
                        if (relatedProduct < 0) return product

                        return {
                            ...product,
                            locationRelationships: [availableProducts[relatedProduct]],
                        }
                    })
                    set({ products: productsWithAvailability })
                } else set({ products: products })
            },
            paginatedProducts: [[]],
            setPaginatedProducts: (products) => set({ paginatedProducts: products }),
            page: 1,
            setPage: (page) => set({ page }),
            pageMax: null,
            setPageMax: (pageMax) => set({ pageMax }),
            isPrevDisabled: true,
            setIsPrevDisabled: (isPrevDisabled) => set({ isPrevDisabled }),
            isNextDisabled: false,
            setIsNextDisabled: (isNextDisabled) => set({ isNextDisabled }),
            filteredProducts: [],
            createPaginatedArray: (products, value) => {
                const chunkArray = []
                if (products.length) {
                    for (let i = 0; i < products.length; i += value) {
                        const chunk = products.slice(i, i + value)
                        chunkArray.push(chunk)
                    }
                    return chunkArray
                }
            },
            setFilteredProducts: (products, value) => {
                const currentLocation = useLocationSlugStore?.getState()?.locationData
                const availableProducts = !currentLocation
                    ? []
                    : products
                          .filter(
                              (product) =>
                                  getLocationAvailability(product, currentLocation?.id)?.available
                          )
                          ?.sort((a, b) => {
                              const aValue = getLocationAvailability(a, currentLocation?.id)?.onTap
                                  ? 1
                                  : 2
                              const bValue = getLocationAvailability(b, currentLocation?.id)?.onTap
                                  ? 1
                                  : 2
                              return aValue - bValue
                          })
                const yearRoundProducts = products.filter(
                    (product) => String(product?.traits?.availability) === 'Year_Round'
                )
                const limitedProducts = products?.filter(
                    (product) => String(product?.traits?.availability) === 'Limited'
                )
                const retiredProducts = products?.filter(
                    (product) => String(product?.traits?.availability) === 'Retired'
                )
                const seasonalProducts = products?.filter(
                    (product) => String(product?.traits?.availability) === 'Seasonal'
                )
                const archivedProducts = products?.filter(
                    (product) => String(product?.traits?.availability) === 'Archived'
                )
                const noAvailabilityProducts = products?.filter(
                    (product) =>
                        !product?.traits?.availability ||
                        String(product?.traits?.availability) === ''
                )

                const allFilteredProducts = [
                    ...availableProducts,
                    ...yearRoundProducts,
                    ...seasonalProducts,
                    ...limitedProducts,
                    ...noAvailabilityProducts,
                    ...retiredProducts,
                    ...archivedProducts,
                ]
                const filteredProducts = allFilteredProducts.filter(
                    (item, index) =>
                        index === allFilteredProducts.findIndex((product) => product.id === item.id)
                )

                const filteredPaginatedProducts = get().createPaginatedArray(
                    filteredProducts,
                    value
                )
                const pageMax = filteredPaginatedProducts?.length || 1
                const isNextDisabled = pageMax === 1
                set({
                    filteredProducts: products,
                    paginatedProducts: filteredPaginatedProducts,
                    pageMax,
                    isNextDisabled,
                })
            },
            styleFilter: '',
            setStyleFilter: (styleFilter) => set({ styleFilter }),
            searchTerm: '',
            setSearchTerm: (searchTerm) => set({ searchTerm }),
            availabilityFilter: '',
            setAvailabilityFilter: (availabilityFilter) => set({ availabilityFilter }),
            resetBrewListStore: () => set({ products: [], availableProducts: [] }),
        }),
        {
            name: 'BrewsListStore',
        }
    )
)
